import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import FieldAgreementList from "./FieldAgreementList";
import LineageGrid from "./LineageGrid";
import MergeEvidenceList from "./MergeEvidenceList";
import type { InvestigatorLineage } from "./lineage.types";

/** Which part to render — the MasteryTab shows one per sub-nav item rather
 *  than stacking all three into one very tall page. */
export type InvestigatorLineageSection =
  | "agreement"
  | "records"
  | "evidence"
  | "all";

interface LineageSectionProps {
  lineage: InvestigatorLineage;
  section?: InvestigatorLineageSection;
}

function Panel({
  title,
  tip,
  extra,
  children,
}: {
  title: string;
  tip: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mcqueenGrotesk text-[13px] font-bold text-[#2B4752]">
            {title}
          </span>
          <Tooltip title={tip}>
            <InfoCircleOutlined className="text-[12px] text-[#556C75]" />
          </Tooltip>
        </div>
        {extra}
      </div>
      {children}
    </div>
  );
}

export default function LineageSection({
  lineage,
  section = "all",
}: LineageSectionProps) {
  const { members, sources, field_clusters, merge_evidence } = lineage;

  if (members.length === 0) {
    return null;
  }

  const show = (key: Exclude<InvestigatorLineageSection, "all">) =>
    section === "all" || section === key;

  const conflictCount = field_clusters.filter(
    (cluster) =>
      cluster.agreement === "conflict" || cluster.agreement === "majority",
  ).length;

  return (
    <div className="flex flex-col gap-4">
      {show("agreement") && (
        <Panel
          title="Source agreement by field"
          tip="Every distinct value each field carries across the raw records merged into this investigator, with the sources that reported it. This is what the mastering pipeline had to reconcile to elect the canonical values shown above."
          extra={
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end">
                <Tooltip title="Raw source records resolved to this golden investigator.">
                  <span className="cursor-help text-[10px] font-medium tracking-wide text-[#556C75] uppercase">
                    Records
                  </span>
                </Tooltip>
                <span className="text-[13px] font-semibold text-[#2B4752] tabular-nums">
                  {members.length}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <Tooltip title="Distinct source systems contributing at least one record.">
                  <span className="cursor-help text-[10px] font-medium tracking-wide text-[#556C75] uppercase">
                    Sources
                  </span>
                </Tooltip>
                <span className="text-[13px] font-semibold text-[#2B4752] tabular-nums">
                  {sources.length}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <Tooltip title="Fields where the sources did not all report the same value.">
                  <span className="cursor-help text-[10px] font-medium tracking-wide text-[#556C75] uppercase">
                    Fields in conflict
                  </span>
                </Tooltip>
                <span className="text-[13px] font-semibold text-[#2B4752] tabular-nums">
                  {conflictCount}
                </span>
              </div>
            </div>
          }
        >
          <FieldAgreementList clusters={field_clusters} />
        </Panel>
      )}

      {show("records") && (
        <Panel
          title="Raw record lineage"
          tip="Every raw source record merged into this golden investigator, side by side and grouped by source system. Cells are tinted green where the record matches what most other records reported for that field, and amber or red where it diverges."
        >
          <LineageGrid
            members={members}
            sources={sources}
            clusters={field_clusters}
          />
        </Panel>
      )}

      {show("evidence") && merge_evidence.length > 0 && (
        <Panel
          title="PI deduplication evidence"
          tip="Match evidence recorded by the PI dedup model for this investigator — the similarity signals and rule basket behind a merge candidate, or the reviewer note behind an over-cluster (split) candidate."
        >
          <MergeEvidenceList evidence={merge_evidence} />
        </Panel>
      )}
    </div>
  );
}
